import { Field } from 'react-final-form';
import { useState } from 'react';
import styled from 'styled-components';
import {
  Autocomplete,
  Avatar,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { Icon } from 'components/ui';
import ListBox from 'components/ListBox';

const FormPatientAutocomplete = (props) => {
  const {
    name,
    isDoctor,
    onChange,
    onInputChange,
    options,
    onScroll,
    onOpen,
    defaultValue,
    value,
    getOptionValue = (v) => v,
    normalizeValue = (v) => v,
    placeholder: customPlaceholder,
    clearInputAfterSelect = false,
    isOptionEqualToValue = (option, value) => {
      return typeof value !== 'object'
        ? getOptionValue(option) === value
        : getOptionValue(option) === getOptionValue(value);
    },
    ...restProps
  } = props;

  const { t } = useTranslation();

  // Manage input text for search and display
  const [inputValue, setInputValue] = useState('');

  const infiniteScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    const scrollHeight = e.target.scrollHeight;
    const clientHeight = e.target.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - 1) {
      onScroll && onScroll();
    }
  };

  return (
    <Field name={name}>
      {({ input, meta }) => {
        return (
          <Autocomplete
            {...restProps}
            freeSolo
            getOptionLabel={(option) =>
              option
                ? isDoctor
                  ? `${option.firstName} ${option.lastName}`
                  : `${option.name.firstName?.[0]} ${option.name.lastName}`
                : ''
            }
            value={value ? getOptionValue(value) : input.value ? getOptionValue(input.value) : null}
            defaultValue={defaultValue}
            options={options}
            isOptionEqualToValue={isOptionEqualToValue}
            inputValue={inputValue}
            onBlur={input.onBlur}
            onChange={(e, selectedValue) => {
              // Handle selection (when user clicks on option)
              input.onChange(normalizeValue(selectedValue));
              
              // Update display to show selected value's name
              if (selectedValue) {
                const displayName = isDoctor 
                  ? `${selectedValue.firstName} ${selectedValue.lastName}`
                  : `${selectedValue.name?.firstName?.[0] || ''} ${selectedValue.name?.lastName || ''}`;
                setInputValue(displayName);
              } else {
                setInputValue('');
              }
              
              // Call parent onChange
              onChange && onChange(e, selectedValue);
              
              // Clear search after selection if configured
              if (clearInputAfterSelect && onInputChange) {
                onInputChange(e, '');
              }
            }}
            onInputChange={(e, newInputValue, reason) => {
              // Always update local inputValue for proper display
              setInputValue(newInputValue);
              
              // Trigger search only for user typing, not MUI internal changes
              if (reason === 'input') {
                // Call onInputChange if provided (new interface)
                if (onInputChange) {
                  onInputChange(e, newInputValue);
                }
                // Also call onChange with target.value for backward compatibility
                if (onChange) {
                  const mockEvent = { target: { value: newInputValue } };
                  onChange(mockEvent);
                }
              } else if (reason === 'clear') {
                if (onInputChange) {
                  onInputChange(e, '');
                }
                if (onChange) {
                  const mockEvent = { target: { value: '' } };
                  onChange(mockEvent);
                }
              }
              // Ignore 'reset' events - they're handled by MUI internally
            }}
            onOpen={onOpen}
            ListboxProps={{ onScroll: (e) => infiniteScroll(e) }}
            ListboxComponent={ListBox}
            loadingText={
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={25} />
              </div>
            }
            popupIcon={<KeyboardArrowDownIcon />}
            renderInput={(params) => {
              return (
                <FormControl style={{ display: 'flex', width: '100%' }}>
                  {input.value ? (
                    <SAvatar src={input.value.photoUrl} />
                  ) : (
                    <Icon
                      icon={isDoctor ? 'stethoscope' : 'user'}
                      style={{
                        position: 'absolute',
                        top: 7,
                        left: 12,
                        fontSize: 24,
                      }}
                    />
                  )}
                  <SInput
                    {...params}
                    onFocus={input.onFocus}
                    error={meta.touched && meta.error}
                    helperText={meta.touched && t(meta.error)}
                    placeholder={
                      customPlaceholder || (isDoctor ? t('Select doctor') : t('Select patient'))
                    }
                  />
                </FormControl>
              );
            }}
            renderOption={(props, option) => {
              return (
                <SFormControl {...props}>
                  <SAvatar isBig src={option.photoUrl} />
                  <Typography
                    height="100%"
                    paddingLeft="60px !important"
                  >
                    {isDoctor
                      ? `${option.firstName} ${option.lastName}`
                      : `${option.name.firstName?.[0]} ${option.name.lastName}`}
                  </Typography>
                </SFormControl>
              );
            }}
          />
        );
      }}
    </Field>
  );
};

const SAvatar = styled(Avatar)`
  && {
    position: absolute;
    left: 12px;
    width: ${({ isBig }) => (isBig ? '32px' : '24px')};
    height: ${({ isBig }) => (isBig ? '32px' : '24px')};
    top: 7px;
    z-index: 1;
  }
`;

const SFormControl = styled('div')`
  && {
    position: relative;
    display: flex;
    width: 100%;
    height: 48px;
    align-items: center;
    cursor: pointer;
    :hover {
      background-color: ${({theme}) => theme.palette.colors.gray100};
    }
  }
`;

const SInput = styled(TextField)`
  && {
    .MuiAutocomplete-clearIndicator {
      display: ${({ value }) => !value && 'none'};
      color: color;
      width: 12px;
      height: 12px;
      margin-right: 10px;
      color: ${({theme}) => theme.palette.colors.blue100};
      :hover {
        background-color: inherit;
      }
    }
    input {
      padding: 0 0 0 40px !important;
    }
    fieldset {
      top: 0;
    }
    legend {
      display: none;
    }
  }
`;

export default FormPatientAutocomplete;